import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { TimeEntry, BreakRecord } from "../../../types/timetracking";
import {
  formatDate,
  formatTime,
  localToUtc,
  utcToLocal,
} from "../../../services/dateTimeService";
import { timeTrackingApi } from "../../../services/timetracking-api";

interface EditTimesheetDialogProps {
  open: boolean;
  entry: TimeEntry | null;
  employeeId?: number;
  employeeName?: string;
  mode: "edit" | "add";
  onSuccess: () => void;
  onCancel: () => void;
}

const EditTimesheetDialog: React.FC<EditTimesheetDialogProps> = ({
  open,
  entry,
  employeeId,
  employeeName,
  mode,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeInDate, setTimeInDate] = useState("");
  const [timeInTime, setTimeInTime] = useState("");
  const [timeOutDate, setTimeOutDate] = useState("");
  const [timeOutTime, setTimeOutTime] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [validationWarning, setValidationWarning] = useState<string | null>(
    null,
  );

  // Break record state
  const [breakRecords, setBreakRecords] = useState<BreakRecord[]>([]);
  const [breakRecordsLoading, setBreakRecordsLoading] = useState(false);
  const [editingBreakId, setEditingBreakId] = useState<number | null>(null);
  const [showAddBreak, setShowAddBreak] = useState(false);
  const [breakStartDate, setBreakStartDate] = useState("");
  const [breakStartTime, setBreakStartTime] = useState("");
  const [breakEndDate, setBreakEndDate] = useState("");
  const [breakEndTime, setBreakEndTime] = useState("");
  const [breakNotes, setBreakNotes] = useState("");
  const [breakAdminNote, setBreakAdminNote] = useState("");

  // Initialize form values when dialog opens
  useEffect(() => {
    if (open) {
      try {
        if (mode === "edit" && entry) {
          // Edit mode: populate with existing entry data
          // Parse time in
          const timeInLocal = utcToLocal(entry.timeIn);
          // Format date as YYYY-MM-DD in local timezone
          const timeInYear = timeInLocal.getFullYear();
          const timeInMonth = String(timeInLocal.getMonth() + 1).padStart(
            2,
            "0",
          );
          const timeInDay = String(timeInLocal.getDate()).padStart(2, "0");
          setTimeInDate(`${timeInYear}-${timeInMonth}-${timeInDay}`);
          // Format time as HH:MM in local timezone
          const timeInHours = String(timeInLocal.getHours()).padStart(2, "0");
          const timeInMinutes = String(timeInLocal.getMinutes()).padStart(
            2,
            "0",
          );
          setTimeInTime(`${timeInHours}:${timeInMinutes}`);

          // Parse time out if exists
          if (entry.timeOut) {
            const timeOutLocal = utcToLocal(entry.timeOut);
            // Format date as YYYY-MM-DD in local timezone
            const timeOutYear = timeOutLocal.getFullYear();
            const timeOutMonth = String(timeOutLocal.getMonth() + 1).padStart(
              2,
              "0",
            );
            const timeOutDay = String(timeOutLocal.getDate()).padStart(2, "0");
            setTimeOutDate(`${timeOutYear}-${timeOutMonth}-${timeOutDay}`);
            // Format time as HH:MM in local timezone
            const timeOutHours = String(timeOutLocal.getHours()).padStart(
              2,
              "0",
            );
            const timeOutMinutes = String(timeOutLocal.getMinutes()).padStart(
              2,
              "0",
            );
            setTimeOutTime(`${timeOutHours}:${timeOutMinutes}`);
          } else {
            setTimeOutDate("");
            setTimeOutTime("");
          }
        } else if (mode === "add") {
          // Add mode: initialize with current date and empty times
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, "0");
          const day = String(now.getDate()).padStart(2, "0");
          setTimeInDate(`${year}-${month}-${day}`);
          setTimeInTime("");
          setTimeOutDate("");
          setTimeOutTime("");
        }

        setAdminNote("");
        setError(null);
        setValidationWarning(null);

        // Reset break record state
        setBreakRecords([]);
        setEditingBreakId(null);
        setShowAddBreak(false);
        resetBreakForm();

        // Load break records for edit mode
        if (mode === "edit" && entry) {
          loadBreakRecords(entry.id);
        }
      } catch (err) {
        setError(t("error.failedToInitializeFormData"));
      }
    }
  }, [open, entry, mode]);

  // Load break records for a time entry
  const loadBreakRecords = async (timeEntryId: number) => {
    try {
      setBreakRecordsLoading(true);
      const records =
        await timeTrackingApi.getBreakRecordsForTimeEntry(timeEntryId);
      setBreakRecords(records);
    } catch (err) {
      console.error("Failed to load break records:", err);
    } finally {
      setBreakRecordsLoading(false);
    }
  };

  // Reset break form fields
  const resetBreakForm = () => {
    setBreakStartDate("");
    setBreakStartTime("");
    setBreakEndDate("");
    setBreakEndTime("");
    setBreakNotes("");
    setBreakAdminNote("");
  };

  // Format break record time for display
  const formatBreakTime = (isoString: string | undefined) => {
    if (!isoString) return "-";
    return formatTime(isoString);
  };

  // Format break duration
  const formatBreakDuration = (minutes: number | undefined) => {
    if (!minutes || minutes === 0) return "-";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Handle edit break click
  const handleEditBreak = (breakRecord: BreakRecord) => {
    setEditingBreakId(breakRecord.id);
    setShowAddBreak(false);

    // Parse break start
    const breakStartLocal = utcToLocal(breakRecord.breakStart);
    const startYear = breakStartLocal.getFullYear();
    const startMonth = String(breakStartLocal.getMonth() + 1).padStart(2, "0");
    const startDay = String(breakStartLocal.getDate()).padStart(2, "0");
    setBreakStartDate(`${startYear}-${startMonth}-${startDay}`);
    const startHours = String(breakStartLocal.getHours()).padStart(2, "0");
    const startMinutes = String(breakStartLocal.getMinutes()).padStart(2, "0");
    setBreakStartTime(`${startHours}:${startMinutes}`);

    // Parse break end if exists
    if (breakRecord.breakEnd) {
      const breakEndLocal = utcToLocal(breakRecord.breakEnd);
      const endYear = breakEndLocal.getFullYear();
      const endMonth = String(breakEndLocal.getMonth() + 1).padStart(2, "0");
      const endDay = String(breakEndLocal.getDate()).padStart(2, "0");
      setBreakEndDate(`${endYear}-${endMonth}-${endDay}`);
      const endHours = String(breakEndLocal.getHours()).padStart(2, "0");
      const endMinutes = String(breakEndLocal.getMinutes()).padStart(2, "0");
      setBreakEndTime(`${endHours}:${endMinutes}`);
    } else {
      setBreakEndDate("");
      setBreakEndTime("");
    }

    setBreakNotes(breakRecord.notes || "");
    setBreakAdminNote("");
  };

  // Handle add break click
  const handleAddBreakClick = () => {
    setShowAddBreak(true);
    setEditingBreakId(null);
    resetBreakForm();

    // Default to time in date
    if (timeInDate) {
      setBreakStartDate(timeInDate);
      setBreakEndDate(timeInDate);
    }
  };

  // Handle cancel break edit/add
  const handleCancelBreakEdit = () => {
    setEditingBreakId(null);
    setShowAddBreak(false);
    resetBreakForm();
  };

  // Handle save break (add or update)
  const handleSaveBreak = async () => {
    if (!breakStartDate || !breakStartTime) {
      setError(t("timetracking.breakEdit.breakStartRequired"));
      return;
    }

    if (!breakAdminNote.trim()) {
      setError(t("timetracking.breakEdit.adminNoteRequired"));
      return;
    }

    // Construct break start ISO string
    const breakStartLocalStr = `${breakStartDate}T${breakStartTime}:00`;
    const breakStartLocal = new Date(breakStartLocalStr);
    const breakStartISO = localToUtc(breakStartLocal);

    // Construct break end ISO string if provided
    let breakEndISO: string | null = null;
    if (breakEndDate && breakEndTime) {
      const breakEndLocalStr = `${breakEndDate}T${breakEndTime}:00`;
      const breakEndLocal = new Date(breakEndLocalStr);

      // Validate break end is after break start
      if (breakEndLocal <= breakStartLocal) {
        setError(t("timetracking.breakEdit.breakEndBeforeStart"));
        return;
      }

      breakEndISO = localToUtc(breakEndLocal);
    }

    try {
      setLoading(true);
      setError(null);

      if (editingBreakId) {
        // Update existing break record
        await timeTrackingApi.updateBreakRecordAsAdmin(
          editingBreakId,
          breakStartISO,
          breakEndISO,
          breakNotes || null,
          breakAdminNote.trim(),
        );
      } else if (entry) {
        // Add new break record
        await timeTrackingApi.addBreakRecordAsAdmin(
          entry.id,
          breakStartISO,
          breakEndISO,
          breakNotes || null,
          breakAdminNote.trim(),
        );
      }

      // Reload break records
      if (entry) {
        await loadBreakRecords(entry.id);
      }

      // Reset form
      handleCancelBreakEdit();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("timetracking.breakEdit.saveFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle delete break
  const handleDeleteBreak = async (breakRecordId: number) => {
    const adminNoteForDelete = window.prompt(
      t("timetracking.breakEdit.deleteAdminNotePrompt"),
    );

    if (!adminNoteForDelete || !adminNoteForDelete.trim()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await timeTrackingApi.deleteBreakRecordAsAdmin(
        breakRecordId,
        adminNoteForDelete.trim(),
      );

      // Reload break records
      if (entry) {
        await loadBreakRecords(entry.id);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("timetracking.breakEdit.deleteFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  // Real-time validation for time in/out combination
  useEffect(() => {
    // Only validate if both time in and time out are provided
    if (timeInDate && timeInTime && timeOutDate && timeOutTime) {
      const timeInLocalStr = `${timeInDate}T${timeInTime}:00`;
      const timeOutLocalStr = `${timeOutDate}T${timeOutTime}:00`;
      const timeInLocal = new Date(timeInLocalStr);
      const timeOutLocal = new Date(timeOutLocalStr);

      if (timeOutLocal <= timeInLocal) {
        setValidationWarning(
          t("timetracking.employeeTimesheet.timeOutBeforeTimeIn"),
        );
      } else {
        setValidationWarning(null);
      }
    } else {
      setValidationWarning(null);
    }
  }, [timeInDate, timeInTime, timeOutDate, timeOutTime, t]);

  const handleSubmit = async () => {
    // Validate admin note
    if (!adminNote.trim()) {
      setError(t("timetracking.employeeTimesheet.adminNoteRequired"));
      return;
    }

    // Validate time in
    if (!timeInDate || !timeInTime) {
      setError(t("timetracking.employeeTimesheet.timeInRequired"));
      return;
    }

    // Validate time in vs time out
    if (timeOutDate && timeOutTime) {
      const timeInLocalStr = `${timeInDate}T${timeInTime}:00`;
      const timeOutLocalStr = `${timeOutDate}T${timeOutTime}:00`;
      const timeInLocal = new Date(timeInLocalStr);
      const timeOutLocal = new Date(timeOutLocalStr);

      if (timeOutLocal <= timeInLocal) {
        setError(t("timetracking.employeeTimesheet.timeOutBeforeTimeIn"));
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      // Construct time in ISO string
      const timeInLocalStr = `${timeInDate}T${timeInTime}:00`;
      const timeInLocal = new Date(timeInLocalStr);
      const timeInISO = localToUtc(timeInLocal); // localToUtc already returns ISO string

      // Construct time out ISO string if provided
      let timeOutISO: string | null = null;
      if (timeOutDate && timeOutTime) {
        const timeOutLocalStr = `${timeOutDate}T${timeOutTime}:00`;
        const timeOutLocal = new Date(timeOutLocalStr);
        timeOutISO = localToUtc(timeOutLocal); // localToUtc already returns ISO string
      }

      if (mode === "edit" && entry) {
        // Call API to update time entry
        await timeTrackingApi.updateTimeEntryAsAdmin(
          entry.id,
          timeInISO,
          timeOutISO,
          adminNote.trim(),
        );
      } else if (mode === "add" && employeeId) {
        // Call API to create time entry
        await timeTrackingApi.createTimeEntryAsAdmin(
          employeeId,
          timeInISO,
          timeOutISO,
          adminNote.trim(),
        );
      }

      onSuccess();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : mode === "edit"
            ? t("timetracking.employeeTimesheet.updateFailed")
            : t("timetracking.employeeTimesheet.createFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === "edit"
          ? t("timetracking.employeeTimesheet.editTimesheet")
          : t("timetracking.employeeTimesheet.addTimesheet")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Employee Info */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t("timetracking.employeeTimesheet.employee")}:{" "}
            <strong>
              {mode === "edit" && entry ? entry.employeeName : employeeName}
            </strong>
          </Typography>
          {mode === "edit" && entry && (
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t("timetracking.employeeTimesheet.workDate")}:{" "}
              <strong>{formatDate(entry.timeIn)}</strong>
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          {validationWarning && (
            <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
              {validationWarning}
            </Alert>
          )}

          {/* Time In */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t("timetracking.table.timeIn")}
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                type="date"
                label={t("common.date")}
                value={timeInDate}
                onChange={(e) => setTimeInDate(e.target.value)}
                fullWidth
                disabled={mode === "edit"}
                InputLabelProps={{ shrink: true }}
                helperText={
                  mode === "edit"
                    ? t("timetracking.employeeTimesheet.dateNotEditable")
                    : undefined
                }
              />
              <TextField
                type="time"
                label={t("common.time")}
                value={timeInTime}
                onChange={(e) => setTimeInTime(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>

          {/* Time Out */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t("timetracking.table.timeOut")}
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                type="date"
                label={t("common.date")}
                value={timeOutDate}
                onChange={(e) => setTimeOutDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="time"
                label={t("common.time")}
                value={timeOutTime}
                onChange={(e) => setTimeOutTime(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>

          {/* Break Records Section - Only in Edit Mode */}
          {mode === "edit" && entry && (
            <>
              <Divider sx={{ mt: 3, mb: 2 }} />
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">
                    {t("timetracking.breakEdit.breakRecords")}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddBreakClick}
                    disabled={
                      loading || showAddBreak || editingBreakId !== null
                    }
                  >
                    {t("timetracking.breakEdit.addBreak")}
                  </Button>
                </Box>

                {/* Break Records Table */}
                {breakRecordsLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : breakRecords.length === 0 && !showAddBreak ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    {t("timetracking.breakEdit.noBreakRecords")}
                  </Typography>
                ) : (
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {t("timetracking.breakEdit.breakStart")}
                          </TableCell>
                          <TableCell>
                            {t("timetracking.breakEdit.breakEnd")}
                          </TableCell>
                          <TableCell>
                            {t("timetracking.breakEdit.duration")}
                          </TableCell>
                          <TableCell align="right">
                            {t("timetracking.table.actions")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {breakRecords.map((breakRecord) => (
                          <TableRow key={breakRecord.id}>
                            <TableCell>
                              {formatBreakTime(breakRecord.breakStart)}
                            </TableCell>
                            <TableCell>
                              {formatBreakTime(breakRecord.breakEnd)}
                            </TableCell>
                            <TableCell>
                              {formatBreakDuration(
                                breakRecord.breakDurationMinutes,
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleEditBreak(breakRecord)}
                                disabled={
                                  loading ||
                                  editingBreakId !== null ||
                                  showAddBreak
                                }
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleDeleteBreak(breakRecord.id)
                                }
                                disabled={
                                  loading ||
                                  editingBreakId !== null ||
                                  showAddBreak
                                }
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Add/Edit Break Form */}
                {(showAddBreak || editingBreakId !== null) && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {editingBreakId
                        ? t("timetracking.breakEdit.editBreak")
                        : t("timetracking.breakEdit.addBreak")}
                    </Typography>

                    {/* Break Start */}
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("timetracking.breakEdit.breakStart")}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                          type="date"
                          label={t("common.date")}
                          value={breakStartDate}
                          onChange={(e) => setBreakStartDate(e.target.value)}
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          type="time"
                          label={t("common.time")}
                          value={breakStartTime}
                          onChange={(e) => setBreakStartTime(e.target.value)}
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                    </Box>

                    {/* Break End */}
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("timetracking.breakEdit.breakEnd")}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                          type="date"
                          label={t("common.date")}
                          value={breakEndDate}
                          onChange={(e) => setBreakEndDate(e.target.value)}
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          type="time"
                          label={t("common.time")}
                          value={breakEndTime}
                          onChange={(e) => setBreakEndTime(e.target.value)}
                          fullWidth
                          size="small"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                    </Box>

                    {/* Break Notes */}
                    <TextField
                      label={t("timetracking.breakEdit.notes")}
                      value={breakNotes}
                      onChange={(e) => setBreakNotes(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ mt: 2 }}
                    />

                    {/* Admin Note for Break */}
                    <TextField
                      label={t("timetracking.breakEdit.adminNote")}
                      value={breakAdminNote}
                      onChange={(e) => setBreakAdminNote(e.target.value)}
                      fullWidth
                      size="small"
                      required
                      sx={{ mt: 2 }}
                      helperText={t("timetracking.breakEdit.adminNoteHelper")}
                    />

                    {/* Break Form Actions */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: 2,
                      }}
                    >
                      <Button
                        size="small"
                        onClick={handleCancelBreakEdit}
                        disabled={loading}
                      >
                        {t("common.cancel")}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveBreak}
                        disabled={
                          loading ||
                          !breakStartDate ||
                          !breakStartTime ||
                          !breakAdminNote.trim()
                        }
                      >
                        {t("common.save")}
                      </Button>
                    </Box>
                  </Paper>
                )}
              </Box>
            </>
          )}

          {/* Admin Note (Mandatory) */}
          <Box sx={{ mt: 3 }}>
            <TextField
              label={t("timetracking.employeeTimesheet.adminNote")}
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              multiline
              rows={3}
              fullWidth
              required
              placeholder={t(
                "timetracking.employeeTimesheet.adminNotePlaceholder",
              )}
              helperText={t("timetracking.employeeTimesheet.adminNoteHelper")}
            />
          </Box>

          {/* Current Admin Note (if exists) */}
          {mode === "edit" && entry?.adminEditNote && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t("timetracking.employeeTimesheet.previousAdminNote")}:
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {entry.adminEditNote}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !adminNote.trim() || !!validationWarning}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTimesheetDialog;
